import React, { useMemo } from "react";
import { Card, ChartSkeleton, Delta, Empty, ErrorState, Kpi, PageHeading, Person, Pill, RowsSkeleton, Table, Workspace } from "../ui";
import { Meter, StackedBars } from "../charts";
import { useFilters } from "../filters";
import { useView } from "../data";
import { AGENT_LABELS, ago, compact, fmt, pct, usd, usdShort } from "../format";
import { fillDays } from "./common";

/** How each figure's dollars were found — one rule, said beside every number. */
export const SOURCE = {
  gateway: { label: "Gateway", tone: "accent", help: "The cost the AI gateway reported for these calls." },
  list: { label: "List price", tone: "neutral", help: "The gateway billed $0 (the provider bills us directly), so tokens × the model's list price from the gateway's live catalogue." },
  unpriced: { label: "Unpriced", tone: "warn", help: "The gateway's catalogue has no price for this model, so its tokens are not in the dollar total." },
};

export function SourcePill({ source }) {
  const s = SOURCE[source] ?? SOURCE.unpriced;
  return (
    <span title={s.help}>
      <Pill tone={s.tone}>{s.label}</Pill>
    </span>
  );
}

/** Who made a group of model calls, in words. */
export function callerOf(entityType, entityName) {
  if (entityType === "agent") return { label: entityName, sub: AGENT_LABELS[entityName] ?? "Agent", kind: "Agent", mono: true };
  if (entityType && entityType.startsWith("workflow")) return { label: "Inside a workflow step", sub: "Calls code makes mid-step: resolvers, field mapping, gates", kind: "Code", mono: false };
  if (entityType) return { label: entityName || entityType, sub: entityType, kind: "Other", mono: true };
  return { label: "Outside an agent", sub: "The router, reply reading, input guard, turn judge", kind: "Code", mono: false };
}

const tokensIn = (t) => t?.input ?? 0;
const cachedShare = (t) => (t?.input ? (t.cacheRead ?? 0) / t.input : null);
const MODEL_SLOTS = ["var(--aa-s1)", "var(--aa-s2)", "var(--aa-s3)", "var(--aa-s4)"];

/**
 * Where the AI money goes: by caller (each agent, code inside a step, code
 * outside any agent), by model, by day, and the most expensive conversations
 * — each of which opens to its model calls one by one.
 */
export default function Cost({ overview }) {
  const { params, prevParams, compare, window: w, set } = useFilters();
  const { data, loading, error } = useView("costs", params);
  const prev = useView("costs", prevParams, null, { skip: !compare }).data;
  const t = data?.total;
  const conversations = overview?.usage?.conversations;

  // The four costliest models get their own colour, in a fixed order; the rest fold into "Other".
  const models = useMemo(() => (data?.byModel ?? []).map((m) => m.model), [data]);
  const shown = models.slice(0, MODEL_SLOTS.length);
  const series = [
    ...shown.map((m, i) => ({ key: m, label: m, color: MODEL_SLOTS[i] })),
    ...(models.length > shown.length ? [{ key: "__other", label: "Other models", color: "var(--aa-neutral)" }] : []),
  ];
  const rows = useMemo(() => {
    const byDay = new Map((data?.daily ?? []).map((d) => [d.day, d]));
    return fillDays([], w.from, w.to).map((d) => {
      const day = byDay.get(d.key);
      const r = { key: d.key, label: d.label, long: d.long };
      for (const m of shown) r[m] = day?.byModel?.[m] ?? 0;
      r.__other = Object.entries(day?.byModel ?? {})
        .filter(([m]) => !shown.includes(m))
        .reduce((s, [, v]) => s + (v ?? 0), 0);
      return r;
    });
  }, [data, w.from, w.to, shown]);

  const d = (cur, before, opts) => (compare && prev?.total ? <Delta cur={cur} prev={before} {...opts} /> : null);

  return (
    <div style={{ display: "grid", gap: 20 }}>
      <PageHeading title="AI cost">
        What the agent's model calls cost: by who made them, by model, by day, and per conversation. Open a conversation to see every call one by one.
      </PageHeading>

      <div className="aa-grid aa-grid-kpi">
        <Kpi
          loading={loading}
          label="AI cost"
          info="Gateway-reported cost where it is above $0; otherwise tokens × the model's list price from the AI Gateway's live catalogue. Models it does not price are left out and named."
          value={t?.usd == null ? "—" : usd(t.usd)}
          delta={d(t?.usd, prev?.total?.usd, { better: "down" })}
          foot={t?.unpriced?.length ? `${t.unpriced.length} model${t.unpriced.length === 1 ? "" : "s"} unpriced` : "every model priced"}
        />
        <Kpi
          loading={loading}
          label="Model calls"
          info="One per model generation. An agent's call covers its whole run, however many round-trips it took."
          value={fmt(t?.calls)}
          delta={d(t?.calls, prev?.total?.calls, { better: null })}
          foot={t?.usd != null && t?.calls ? `${usd(t.usd / t.calls)} per call` : null}
        />
        <Kpi
          loading={loading}
          label="Tokens in"
          value={compact(tokensIn(t?.tokens))}
          delta={d(tokensIn(t?.tokens), tokensIn(prev?.total?.tokens), { better: "down" })}
          foot={cachedShare(t?.tokens) == null ? null : `${pct(cachedShare(t.tokens))} read from cache`}
        />
        <Kpi
          loading={loading}
          label="Cost per conversation"
          value={t?.usd != null && conversations ? usd(t.usd / conversations) : "—"}
          foot={conversations ? `over ${fmt(conversations)} conversations` : null}
          info="AI cost divided by the conversations active in this period."
        />
      </div>

      <Card title="Cost per day" subtitle="By model · priced at list (a daily series carries no gateway-reported cost)">
        {loading ? (
          <ChartSkeleton h={260} />
        ) : error ? (
          <ErrorState error={error} />
        ) : (data?.daily ?? []).some((x) => x.usd > 0) ? (
          <StackedBars rows={rows} series={series} height={240} format={usd} axisFormat={usdShort} />
        ) : (
          <Empty icon="dollar" title="No priced model calls in this period">
            {t?.calls ? "Calls were recorded, but none of their models has a list price in the gateway's catalogue." : "Nothing recorded yet — Mastra tracing must be on where the agent runs."}
          </Empty>
        )}
      </Card>

      <Card title="By caller" subtitle="Who made the calls · share of the priced total" flush>
        {loading ? (
          <RowsSkeleton />
        ) : data?.byCaller?.length ? (
          <Table
            defaultSort={{ key: "usd", dir: "desc" }}
            columns={[
              {
                key: "caller",
                label: "Caller",
                sort: (r) => callerOf(r.entityType, r.entityName).label,
                render: (r) => {
                  const c = callerOf(r.entityType, r.entityName);
                  return (
                    <span>
                      <span className={c.mono ? "aa-mono" : undefined} style={{ fontWeight: 600, fontSize: 13 }}>
                        {c.label}
                      </span>
                      <span className="aa-sub">{c.sub}</span>
                    </span>
                  );
                },
              },
              { key: "kind", label: "Kind", sort: (r) => callerOf(r.entityType, r.entityName).kind, render: (r) => <Pill tone="neutral">{callerOf(r.entityType, r.entityName).kind}</Pill> },
              { key: "calls", label: "Calls", align: "right", render: (r) => fmt(r.calls) },
              { key: "in", label: "Tokens in", align: "right", sort: (r) => r.tokens.input, render: (r) => <Tokens t={r.tokens} /> },
              { key: "out", label: "Tokens out", align: "right", sort: (r) => r.tokens.output, render: (r) => compact(r.tokens.output) },
              { key: "models", label: "Models", sortable: false, render: (r) => <span className="aa-mono" style={{ color: "var(--aa-ink-2)" }}>{r.models.map((m) => m.model).join(", ")}</span> },
              { key: "usd", label: "Cost", align: "right", render: (r) => <Money v={r.usd} total={t?.usd} /> },
            ]}
            rows={data.byCaller.map((r) => ({ ...r, id: `${r.entityType}|${r.entityName}` }))}
          />
        ) : (
          <Empty icon="dollar" title="No model calls recorded in this period" />
        )}
      </Card>

      <div className="aa-grid aa-grid-2">
        <Card title="By model" subtitle="How each model's dollars were found" flush>
          {loading ? (
            <RowsSkeleton rows={3} />
          ) : data?.byModel?.length ? (
            <Table
              defaultSort={{ key: "usd", dir: "desc" }}
              columns={[
                { key: "model", label: "Model", render: (r) => <span className="aa-mono">{r.model === "unknown" ? "Not named (older records)" : r.model}</span> },
                { key: "calls", label: "Calls", align: "right", render: (r) => fmt(r.calls) },
                { key: "in", label: "In", align: "right", sort: (r) => r.tokens.input, render: (r) => <Tokens t={r.tokens} /> },
                { key: "out", label: "Out", align: "right", sort: (r) => r.tokens.output, render: (r) => compact(r.tokens.output) },
                { key: "source", label: "Priced by", sort: (r) => r.groups?.[0]?.source, render: (r) => <SourcePill source={sourceOf(r.sources)} /> },
                { key: "usd", label: "Cost", align: "right", render: (r) => (r.usd == null ? <span style={{ color: "var(--aa-faint)" }}>—</span> : usd(r.usd)) },
              ]}
              rows={data.byModel.map((m) => ({ ...m, id: m.model }))}
            />
          ) : (
            <Empty compact icon="dollar" title="No model usage" />
          )}
        </Card>

        <Card title="How the dollars were found" subtitle={data?.prices?.asOf ? `List prices from the AI Gateway catalogue, fetched ${ago(data.prices.asOf)}` : "AI Gateway catalogue not reached yet"}>
          <div style={{ display: "grid", gap: 12 }}>
            {["gateway", "list", "unpriced"].map((k) => (
              <div key={k} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                <SourcePill source={k} />
                <div style={{ flex: 1, fontSize: 13, color: "var(--aa-ink-2)" }}>{SOURCE[k].help}</div>
                <span className="aa-num" style={{ fontWeight: 600 }}>
                  {fmt(t?.sources?.[k] ?? 0)}
                </span>
              </div>
            ))}
            {t?.unpriced?.length > 0 && (
              <div style={{ fontSize: 12, color: "var(--aa-muted)", borderTop: "1px solid var(--aa-border)", paddingTop: 10 }}>
                Unpriced: <span className="aa-mono">{t.unpriced.join(", ")}</span>
              </div>
            )}
          </div>
        </Card>
      </div>

      <Card title="Most expensive conversations" subtitle="Click one to see every model call in it" flush>
        {loading ? (
          <RowsSkeleton />
        ) : data?.conversations?.length ? (
          <Table
            defaultSort={{ key: "usd", dir: "desc" }}
            onRow={(r) => set({ session: r.sessionId })}
            columns={[
              {
                key: "title",
                label: "Conversation",
                max: 380,
                sortable: false,
                render: (r) => (
                  <span style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", fontWeight: 500 }}>
                    {r.title?.replace(/\*\*/g, "") || r.first_prompt || <span className="aa-mono">{r.sessionId}</span>}
                  </span>
                ),
              },
              { key: "account_id", label: "Workspace", sortable: false, render: (r) => <Workspace id={r.account_id} /> },
              { key: "user_id", label: "Person", sortable: false, render: (r) => <Person id={r.user_id} compact /> },
              { key: "calls", label: "Calls", align: "right", render: (r) => fmt(r.calls) },
              { key: "in", label: "Tokens in", align: "right", sort: (r) => r.tokens.input, render: (r) => <Tokens t={r.tokens} /> },
              { key: "usd", label: "Cost", align: "right", render: (r) => <Money v={r.usd} total={t?.usd} /> },
            ]}
            rows={data.conversations.map((r) => ({ ...r, id: r.sessionId }))}
          />
        ) : (
          <Empty compact icon="conversations" title="No conversation used a model in this period" />
        )}
      </Card>
    </div>
  );
}

function sourceOf(sources) {
  if (!sources) return "unpriced";
  if (sources.gateway && !sources.list) return "gateway";
  if (sources.list) return "list";
  return "unpriced";
}

/** Tokens in, with the share read from cache under it. */
export function Tokens({ t }) {
  const c = cachedShare(t);
  return (
    <span title={`${fmt(t?.input)} in · ${fmt(t?.cacheRead)} cached read · ${fmt(t?.cacheWrite)} cache write`}>
      {compact(t?.input)}
      {c != null && c > 0 && <span className="aa-sub">{pct(c)} cached</span>}
    </span>
  );
}

/** A cost with its share of the total as a thin meter. */
function Money({ v, total }) {
  if (v == null) return <span style={{ color: "var(--aa-faint)" }} title="No price for this model">unpriced</span>;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 10, justifyContent: "flex-end" }}>
      {total ? <Meter value={v / total} width={48} /> : null}
      <span style={{ minWidth: 64, fontWeight: 600 }}>{usd(v)}</span>
    </span>
  );
}
